import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { iconForKey } from './nodeTypes';
import type { WorkflowNodeData } from './types';

// Custom React Flow node. Every node exposes both a target (left) and source
// (right) handle so any two nodes can be connected regardless of kind. The
// stored iconKey is rehydrated to a Lucide component here — the persisted graph
// never holds JSX.
function WorkflowNodeImpl({ data, selected }: NodeProps) {
  const d = data as WorkflowNodeData;
  const Icon = iconForKey(d.iconKey);

  return (
    <div
      className={`w-[190px] bg-white border rounded-xl shadow-md transition duration-150 ${
        selected ? 'border-purple-600 ring-2 ring-purple-100' : 'border-[#EDEDEB] hover:border-slate-300'
      }`}
    >
      <Handle type="target" position={Position.Left} className="!w-2.5 !h-2.5 !bg-slate-400 !border-2 !border-white" />

      <div className="p-2 border-b border-slate-100 flex items-center gap-1.5 bg-slate-50/50 rounded-t-xl">
        <div className="p-1 rounded bg-white border border-slate-100 shadow-3xs shrink-0">
          <Icon className={d.iconClass ?? 'w-4 h-4'} />
        </div>
        <span className="text-[10px] font-bold text-slate-700 font-sans truncate pr-1">{d.label}</span>
      </div>

      <div className="p-2.5 space-y-1.5">
        <span className="inline-block text-[8px] font-mono font-bold bg-purple-50 border border-purple-100 text-purple-700 px-1.5 py-0.5 rounded uppercase">
          {d.kind}
        </span>
        <p className="text-[9px] text-slate-500 leading-normal line-clamp-2">{d.description}</p>
      </div>

      <Handle type="source" position={Position.Right} className="!w-2.5 !h-2.5 !bg-purple-600 !border-2 !border-white" />
    </div>
  );
}

export const WorkflowNodeComponent = memo(WorkflowNodeImpl);
